import { useState, useMemo, useCallback } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useHotspotRewards } from './useHotspotRewards'
import { subDays, format } from 'date-fns'
import { isDateInRewardDataGap } from '../../lib/rewardDataGap'
import { RewardDataGapNotice } from '../../components/RewardDataGapNotice'

// --- Types ---
type Period = '7d' | '30d' | '90d' | '1y' | 'all'

type Props = {
  id: string
}

type ChartDataEntry = {
  date: string
  hnt: number
  iot: number
  mobile: number
  // Affiliate existiert nicht als Tagesdaten (nur %-Anteil auf UserHotspot) — Linie entfernt
}

type TooltipPayload = {
  payload?: ChartDataEntry
}

// --- Colors ---
const COLORS = {
  hnt: '#22c55e',
  iot: '#06b6d4',
  mobile: '#a855f7',
}

// --- Hilfsfunktionen ---
function shortDate(dateStr: string): string {
  // '2026-05-10' → '10.05'
  const parts = dateStr.split('-')
  if (parts.length !== 3) return dateStr
  return `${parts[2]}.${parts[1]}`
}

const PERIOD_DAYS: Record<Exclude<Period, 'all'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '1y': 365,
}

/** 'all' liefert bewusst kein from/to — das Backend gibt dann die volle Historie zurück. */
function getPeriodDates(period: Period): { from?: string; to?: string } {
  if (period === 'all') return {}
  const now = new Date()
  return {
    from: format(subDays(now, PERIOD_DAYS[period]), 'yyyy-MM-dd'),
    to: format(now, 'yyyy-MM-dd')
  }
}

// --- Zeitraum-Umschalter (in beiden Render-Zweigen genutzt) ---

type PeriodSwitcherProps = {
  period: Period
  onChange: (p: Period) => void
  compact?: boolean
}

function PeriodSwitcher({ period, onChange, compact = false }: PeriodSwitcherProps) {
  const { t } = useTranslation('hotspots')
  const options: { key: Period; label: string }[] = [
    { key: '7d', label: '7d' },
    { key: '30d', label: '30d' },
    { key: '90d', label: '90d' },
    { key: '1y', label: t('rewardsChart.period1y') },
    { key: 'all', label: t('rewardsChart.periodAll') },
  ]

  return (
    <div
      role="group"
      aria-label={t('rewardsChart.periodLabel')}
      style={
        compact
          ? { display: 'flex', gap: 8 }
          : { display: 'flex', gap: 4, background: 'var(--bg-subtle)', padding: 4, borderRadius: 'var(--radius-md)' }
      }
    >
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          className={`btn btn-sm ${period === opt.key ? 'btn-primary' : 'btn-ghost'}`}
          aria-pressed={period === opt.key}
          onClick={() => onChange(opt.key)}
          style={!compact && period !== opt.key ? { color: 'var(--text-muted)' } : {}}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

/** Lokalisiertes Label des aktiven Zeitraums (für Hinweistexte). */
function usePeriodLabel(period: Period): string {
  const { t } = useTranslation('hotspots')
  if (period === 'all') return t('rewardsChart.periodAll')
  if (period === '1y') return t('rewardsChart.period1y')
  return period
}

// --- Custom Tooltip ---
function ChartTooltip({ payload }: { active?: boolean; payload?: TooltipPayload[] }) {
  if (!payload || payload.length === 0) return null
  const entry = payload[0]?.payload
  if (!entry) return null
  return (
    <div
      className="card card-pad"
      style={{ padding: '8px 12px', fontSize: 12, minWidth: 160 }}
      aria-live="polite"
    >
      <div className="muted" style={{ marginBottom: 4 }}>
        {entry.date}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: COLORS.hnt, flexShrink: 0 }} />
        <span className="num-sm c-hnt">{entry.hnt != null ? entry.hnt.toFixed(4) : '—'} HNT</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: COLORS.iot, flexShrink: 0 }} />
        <span style={{ color: COLORS.iot, fontSize: 11 }}>{entry.iot != null ? entry.iot.toFixed(2) : '—'} IOT</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: COLORS.mobile, flexShrink: 0 }} />
        <span style={{ color: COLORS.mobile, fontSize: 11 }}>{entry.mobile != null ? entry.mobile.toFixed(2) : '—'} MOBILE</span>
      </div>
    </div>
  )
}

// --- Loading-Skeleton ---
function ChartSkeleton() {
  const { t } = useTranslation('hotspots')
  return (
    <div
      className="skeleton"
      style={{ height: 250 }}
      aria-busy="true"
      aria-label={t('rewardChart.skeletonLabel')}
    />
  )
}

// --- Custom Legend ---
type LegendItemProps = {
  id: string
  label: string
  color: string
  visible: boolean
  onToggle: (id: string) => void
}

function LegendItem({ id, label, color, visible, onToggle }: LegendItemProps) {
  const { t } = useTranslation('hotspots')
  const toggleLabel = visible ? t('rewardsChart.toggleHide') : t('rewardsChart.toggleShow')
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        opacity: visible ? 1 : 0.4,
        transition: 'opacity 0.2s',
        borderRadius: 'var(--radius-sm)',
      }}
      aria-pressed={visible}
      title={`${toggleLabel} ${label}`}
    >
      <span style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: 'var(--text)' }}>{label}</span>
    </button>
  )
}

export function HotspotRewardsChart({ id }: Props) {
  const { t } = useTranslation('hotspots')
  const [period, setPeriod] = useState<Period>('30d')
  const periodLabel = usePeriodLabel(period)

  const dates = useMemo(() => getPeriodDates(period), [period])

  // 'all' hat kein festes from/to (volle Historie, Grenzen unbekannt) — dafür
  // kein Gap-Hinweis. Für die begrenzten Presets ist die Lücke (17 Monate)
  // deutlich breiter als jedes hier wählbare Fenster (max. 1 Jahr), ein
  // Endpunkt-Check reicht daher für Überlappung.
  const isDataGap =
    !!dates.from && (isDateInRewardDataGap(dates.from) || isDateInRewardDataGap(dates.to ?? dates.from))

  const { data: rewards, isLoading, isError } = useHotspotRewards(id, dates)

  // Hidden series state
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())

  const toggleSeries = useCallback((seriesId: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev)
      if (next.has(seriesId)) {
        next.delete(seriesId)
      } else {
        next.add(seriesId)
      }
      return next
    })
  }, [])

  // Calculate totals
  const totals = useMemo(() => {
    if (!rewards) return { hnt: 0, iot: 0, mobile: 0 }
    return rewards.reduce(
      (acc, r) => ({
        hnt: acc.hnt + (r.hnt || 0),
        iot: acc.iot + (r.iot || 0),
        mobile: acc.mobile + (r.mobile || 0),
      }),
      { hnt: 0, iot: 0, mobile: 0 }
    )
  }, [rewards])

  if (isLoading) return <ChartSkeleton />

  if (isError || !rewards || rewards.length === 0) {
    return (
      <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="section-heading" style={{ marginBottom: 0 }}>
            {t('rewardsChart.heading')}
          </h3>
          <PeriodSwitcher period={period} onChange={setPeriod} compact />
        </div>
        {isDataGap && <RewardDataGapNotice />}
        <div
          style={{ height: 250, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', justifyContent: 'center' }}
          aria-label={t('rewardsChart.noDataLabel')}
        >
          <span className="muted">
            {isError
              ? t('rewardsChart.noData')
              : period === 'all'
                ? t('rewardsChart.noDataAll')
                : t('rewardsChart.noDataInRange', { range: periodLabel })}
          </span>
          {!isError && period !== 'all' && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPeriod('all')}>
              {t('rewardsChart.showAll')}
            </button>
          )}
        </div>
      </div>
    )
  }

  // Sort chronological for chart (oldest first)
  const chartData = [...rewards].sort((a, b) => a.date.localeCompare(b.date))

  return (
    <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="section-heading" style={{ marginBottom: 0 }}>
          {t('rewardsChart.heading')}
        </h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'right' }}>
            <div className="num-sm c-hnt" style={{ fontWeight: 600 }}>
              {totals.hnt.toFixed(2)} HNT
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              {totals.iot.toFixed(0)} IOT · {totals.mobile.toFixed(0)} MOBILE
            </div>
          </div>
          <PeriodSwitcher period={period} onChange={setPeriod} />
        </div>
      </div>

      {/* Interactive Legend */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} role="group" aria-label={t('rewardsChart.seriesToggleLabel')}>
        <LegendItem id="hnt" label="HNT" color={COLORS.hnt} visible={!hiddenSeries.has('hnt')} onToggle={toggleSeries} />
        <LegendItem id="iot" label="IOT" color={COLORS.iot} visible={!hiddenSeries.has('iot')} onToggle={toggleSeries} />
        <LegendItem id="mobile" label="MOBILE" color={COLORS.mobile} visible={!hiddenSeries.has('mobile')} onToggle={toggleSeries} />
      </div>

      {isDataGap && <RewardDataGapNotice />}

      <div aria-label={t('rewardChart.chartLabel')} style={{ marginTop: 8 }}>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={shortDate}
              tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
              tickLine={false}
              axisLine={false}
              minTickGap={20}
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
              tickFormatter={(v: number) => v.toFixed(2)}
              tickLine={false}
              axisLine={false}
              domain={['auto', 'auto']}
            />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--border)', opacity: 0.2 }} />
            <Bar
              dataKey="hnt"
              fill={COLORS.hnt}
              radius={[2, 2, 0, 0]}
              hide={hiddenSeries.has('hnt')}
              name="HNT"
            />
            <Bar
              dataKey="iot"
              fill={COLORS.iot}
              radius={[2, 2, 0, 0]}
              hide={hiddenSeries.has('iot')}
              name="IOT"
            />
            <Bar
              dataKey="mobile"
              fill={COLORS.mobile}
              radius={[2, 2, 0, 0]}
              hide={hiddenSeries.has('mobile')}
              name="MOBILE"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}